import { ModuleRendering, SlotRendering } from '@frontend/common-types';
import { Spinner } from '@frontend/components/common';
import { Module } from '@frontend/module/types';
import { SpotRenderingClient } from '@frontend/spot/api';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { ChooseModuleModal } from '../../../components/choose-module-modal/choose-module-modal.component';
import { SpotLayout } from '../../../spot-rendering';
import { ConfirmOpenSlotHint } from '../../components';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import type { ColliConfirmOpenSlotsSerialViewProps } from '../../hooks/use-colli-confirm-open-slots-serial';

interface SerialConfirmOpenSlotsScreenProps {
    id: string;
    headerTitle: ReactNode;
    viewProps: ColliConfirmOpenSlotsSerialViewProps;
    openSlotLabel: ReactNode;
}

export const SerialConfirmOpenSlotsScreen = (props: SerialConfirmOpenSlotsScreenProps) => {
    const { viewProps } = props;
    const { progress } = viewProps;
    const colli = progress.colli;

    if (!viewProps.accountId || !viewProps.spotId) {
        return (
            <WizardScreenContainer
                id={props.id}
                header={{ title: props.headerTitle }}
                containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-8 self-stretch overflow-y-scroll p-6 pb-20'>
                <Spinner />
            </WizardScreenContainer>
        );
    }

    return (
        <WizardScreenContainer
            id={props.id}
            header={{ title: props.headerTitle }}
            footer={{
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.submitDisabled,
                submitLabel: props.openSlotLabel
            }}
            containerClassName='bg-background-default flex flex-1 flex-col items-center justify-start gap-8 self-stretch overflow-y-scroll p-6 pb-20'>
            <div className='flex flex-col items-start gap-3 self-stretch'>
                {viewProps.resolveError ? (
                    <div className='text-foreground-default text-base font-medium'>
                        <FormattedMessage
                            id='kiosk.wizard.confirm-open-slots-serial.no-packages'
                            defaultMessage='No parcels are left to open at this distriSPOT.'
                            description='Error when serial confirm has no remaining openable package'
                        />
                    </div>
                ) : (
                    <div className='flex items-start justify-between gap-4 self-stretch'>
                        <ConfirmOpenSlotHint show={!!viewProps.selectedSlotId} />
                        {progress.total > 1 ? (
                            <div className='flex flex-col items-end text-right'>
                                <span className='text-foreground-default text-base font-medium'>
                                    <FormattedMessage
                                        id='kiosk.wizard.confirm-open-slots-serial.progress'
                                        defaultMessage='Parcel {index}/{total}'
                                        description='Progress through serial open-SLOT loop for collis when more than one parcel'
                                        values={{ index: progress.index, total: progress.total }}
                                    />
                                </span>
                                {colli?.label ? <span className='text-brand-muted text-xs'>{colli.label}</span> : null}
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            <SpotLayout<ModuleRendering, SlotRendering>
                renderMode='module'
                fetchSpotData={(arg) => SpotRenderingClient.fetchSpotRendering(viewProps.accountId!, viewProps.spotId!, arg)}
                fetchModuleData={(accountId, spotId, moduleId) => SpotRenderingClient.fetchSpotModuleRendering(accountId, spotId, moduleId)}
                slot={{ renderElement: viewProps.slotComponent }}
                enableSlotHover={false}
                module={viewProps.currentModule || undefined}
                onModuleNameClick={() => viewProps.setSelectModuleModalOpen(true)}
                onSelectedModuleChange={(moduleId) => {
                    const module = viewProps.modules?.find((m: Module) => m.id === moduleId);
                    if (module) viewProps.setCurrentModule(module);
                }}
            />

            <ChooseModuleModal
                modules={viewProps.modules}
                show={viewProps.selectModuleModalOpen}
                onClose={() => viewProps.setSelectModuleModalOpen(false)}
                onSelectModule={viewProps.setCurrentModule}
            />
        </WizardScreenContainer>
    );
};
